import { Modal } from '@appkit4/react-components/modal';
import { Badge } from '@appkit4/react-components/badge';
import { Button } from '@appkit4/react-components/button';
import { Dispatch, SetStateAction } from 'react';
import { Column, Table } from '@appkit4/react-components';
import {
    getGetV1SelfDeclarationPaginatedHistoryQueryKey,
    getGetV1SelfDeclarationQueryKey,
    useDeleteV1SelfDeclarationSubmissionId,
} from '../../../../../api/generated/endpoint';
import { PlannedSubmission } from '../../../../../api/generated/models/plannedSubmission';
import DateCellRenderer from 'components/pages/history/DateCellRenderer';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useGenericErrorToast from 'helpers/useGenericErrorToast';
import useToast from 'helpers/useToast';
import useLocale from '../../../../../locale/useLocale';
import { GetV1SelfDeclarationPaginatedHistoryParams } from 'api/generated/models';
import renderReasonForAbsence from 'pages/history/renderReasonForAbsence';

type ConfirmDeleteModalProps = {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    submissionToDelete: PlannedSubmission | null;
    historyParams: GetV1SelfDeclarationPaginatedHistoryParams;
};

const ConfirmDeleteModal = ({
    visible,
    setVisible,
    submissionToDelete,
    historyParams,
}: ConfirmDeleteModalProps) => {
    const queryClient = useQueryClient();
    const successToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();
    const {
        mutateAsync: deleteSubmission,
        isPending: deleteSubmissionIsPending,
    } = useDeleteV1SelfDeclarationSubmissionId({
        mutation: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey:
                        getGetV1SelfDeclarationPaginatedHistoryQueryKey(
                            historyParams
                        ),
                });
                await queryClient.invalidateQueries({
                    queryKey: getGetV1SelfDeclarationQueryKey(),
                });
                successToast(
                    'Success',
                    'Submission deleted successfully',
                    'success'
                );
            },
            onError: (error: AxiosError) => {
                showGenericErrorToast(error);
                return error;
            },
        },
    });

    const handleConfirmDelete = async () => {
        if (submissionToDelete) {
            await deleteSubmission({
                submissionId: submissionToDelete.submissionId!,
            });
            setVisible(false);
        }
    };

    return (
        <Modal
            maskBodyPadding={false}
            visible={visible}
            title={'Delete this declaration?'}
            ariaLabel={'Are you sure?'}
            onCancel={() => setVisible(false)}
            modalStyle={{ width: '60rem' }}
            footerStyle={{
                paddingTop: '8px',
                marginTop: '-8px',
                minHeight: '64px',
            }}
            icons={''}
            header={<Badge type="warning" value="Delete" />}
            footer={
                <>
                    <Button onClick={() => setVisible(false)} kind="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        loading={deleteSubmissionIsPending}
                    >
                        Delete
                    </Button>
                </>
            }
            bodyStyle={{ minHeight: '92px' }}
        >
            <p>Are you sure you want to delete the following entry:</p>
            {submissionToDelete !== null && (
                <Table
                    originalData={[submissionToDelete]}
                    hasTitle
                    className="mt-3"
                >
                    <Column field="declarationType">
                        {l('_historyTableCircumstanceType')}
                    </Column>
                    <Column
                        field="reasonForAbsence"
                        renderCell={renderReasonForAbsence}
                    >
                        {l('_historyTableCircumstanceDetail')}
                    </Column>
                    <Column field="dateRanges" renderCell={DateCellRenderer}>
                        {l('_historyTableDates')}
                    </Column>
                    <Column field="totalDaysAbsent">
                        {l('_historyTableDays')}
                    </Column>
                </Table>
            )}
        </Modal>
    );
};

export default ConfirmDeleteModal;
